/**
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Package manager detection utilities
 */
import { fileExists } from './fs.js';
import { join } from 'path';
import type { PackageManager } from '../types/index.js';

/**
 * Detect which package manager is being used in a project
 */
export const detectPackageManager = (cwd: string): PackageManager => {
  // Check for lock files in order of preference
  if (fileExists(join(cwd, 'pnpm-lock.yaml'))) {
    return 'pnpm';
  }

  if (fileExists(join(cwd, 'bun.lockb'))) {
    return 'bun';
  }

  if (fileExists(join(cwd, 'yarn.lock'))) {
    return 'yarn';
  }

  if (fileExists(join(cwd, 'package-lock.json'))) {
    return 'npm';
  }

  // Default to npm if package.json exists
  if (fileExists(join(cwd, 'package.json'))) {
    return 'npm';
  }

  return null;
};

/**
 * Get the install command for a package manager
 */
export const getInstallCommand = (pm: PackageManager): string => {
  switch (pm) {
    case 'pnpm':
      return 'pnpm install';
    case 'yarn':
      return 'yarn install';
    case 'bun':
      return 'bun install';
    case 'npm':
      return 'npm install';
    default:
      return 'npm install';
  }
};

/**
 * Get the run command for a package manager
 */
export const getRunCommand = (pm: PackageManager, script: string): { cmd: string; args: string[] } => {
  switch (pm) {
    case 'pnpm':
      return { cmd: 'pnpm', args: ['run', script] };
    case 'yarn':
      return { cmd: 'yarn', args: [script] };
    case 'bun':
      return { cmd: 'bun', args: ['run', script] };
    case 'npm':
      return { cmd: 'npm', args: ['run', script] };
    default:
      return { cmd: 'npm', args: ['run', script] };
  }
};

